import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Bell, Check, CheckCheck, Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useNotifikationer } from "@/contexts/NotifikationerContext";
import { DashboardHeader } from "@/components/DashboardHeader";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtTid(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const nu = new Date();
  const diffMs = nu - d;
  const diffMin = Math.floor(diffMs / 60000);
  const diffH = Math.floor(diffMin / 60);
  const diffD = Math.floor(diffH / 24);
  if (diffMin < 1) return "Lige nu";
  if (diffMin < 60) return `${diffMin} min siden`;
  if (diffH < 24) return `${diffH} t siden`;
  if (diffD < 7) return `${diffD} dag${diffD !== 1 ? "e" : ""} siden`;
  return d.toLocaleDateString("da-DK", { day: "numeric", month: "short" });
}

const TYPE_IKON = {
  besked:          "💬",
  ny_vagt:         "📅",
  vagtaendring:    "🔄",
  ferie_godkendt:  "✅",
  ferie_afvist:    "❌",
};

export default function NotifikationerPage() {
  const { user, token, logout } = useAuth();
  const { setUlæstNotifikationer } = useNotifikationer();
  const [notifikationer, setNotifikationer] = useState([]);
  const [loading, setLoading] = useState(true);
  const [markererAlle, setMarkererAlle] = useState(false);

  const hentNotifikationer = useCallback(async () => {
    if (!token) return;
    try {
      const { data } = await axios.get(`${API}/notifikationer`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifikationer(data || []);
    } catch {
      // stille fejl
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    hentNotifikationer();
  }, [hentNotifikationer]);

  const markerLaest = async (id) => {
    try {
      await axios.post(`${API}/notifikationer/${id}/laest`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifikationer((prev) =>
        prev.map((n) => n._id === id ? { ...n, laest: true } : n)
      );
      setUlæstNotifikationer((c) => Math.max(0, c - 1));
    } catch {
      // stille fejl
    }
  };

  const markerAlleLaest = async () => {
    setMarkererAlle(true);
    try {
      await axios.post(`${API}/notifikationer/laes-alle`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifikationer((prev) => prev.map((n) => ({ ...n, laest: true })));
      setUlæstNotifikationer(0);
    } catch {
      // stille fejl
    } finally {
      setMarkererAlle(false);
    }
  };

  const ulæste = notifikationer.filter((n) => !n.laest).length;

  return (
    <div className="min-h-screen bg-background">
      <DashboardHeader user={user} onLogout={() => { logout(); window.location.href = "/login"; }} />

      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-muted-foreground" />
            <h1 className="text-lg font-semibold">Notifikationer</h1>
            {ulæste > 0 && (
              <span className="bg-[#FF2A6D] text-white text-[11px] font-bold px-2 py-0.5 rounded-full">
                {ulæste}
              </span>
            )}
          </div>
          {ulæste > 0 && (
            <button
              onClick={markerAlleLaest}
              disabled={markererAlle}
              className="flex items-center gap-1.5 text-[13px] text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
            >
              {markererAlle
                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                : <CheckCheck className="w-3.5 h-3.5" />}
              Markér alle som læst
            </button>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : notifikationer.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-muted-foreground gap-3">
            <Bell className="w-10 h-10 opacity-20" />
            <p className="text-sm">Ingen notifikationer de seneste 30 dage</p>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {notifikationer.map((n) => (
              <div
                key={n._id}
                className={`flex items-start gap-3 p-4 rounded-xl border transition-colors ${
                  n.laest
                    ? "bg-background border-border"
                    : "bg-[#FF2A6D]/5 border-[#FF2A6D]/20"
                }`}
              >
                <span className="text-xl shrink-0 mt-0.5">
                  {TYPE_IKON[n.type] || "🔔"}
                </span>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium leading-snug ${n.laest ? "text-foreground" : "text-foreground"}`}>
                    {n.titel}
                  </p>
                  {n.tekst && (
                    <p className="text-[13px] text-muted-foreground mt-0.5">{n.tekst}</p>
                  )}
                  <p className="text-[12px] text-muted-foreground mt-1">{fmtTid(n.oprettet)}</p>
                </div>
                {!n.laest && (
                  <button
                    onClick={() => markerLaest(n._id)}
                    className="shrink-0 p-1.5 rounded-lg hover:bg-secondary transition-colors"
                    title="Markér som læst"
                  >
                    <Check className="w-3.5 h-3.5 text-muted-foreground" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
